import { IconChevronDown } from '@/theme/icons';
import rem from '@/utils/rem';
import { Box, Button, Flex, Icon, Menu, MenuButton, MenuItem, MenuList, StyleProps, Text } from '@chakra-ui/react';
import { useCallback, useState } from 'react';

type CustomRecurrenceMenuProps = {
  onOpen?: () => void;
  onClose?: () => void;
  isLoading?: boolean;
  categoryId?: string;
  recurrence: string | null;
  setSelectedRecurrence?: (recurrence: any) => void;
  readonly?: boolean;
  width?: StyleProps['width'];
  showCategoryIcon?: boolean;
  variant?: 'default' | 'bordered';
};

const CustomRecurrenceMenu = ({
  onOpen,
  onClose,
  recurrence,
  setSelectedRecurrence,
  readonly,
  width = rem(80),
  variant = 'default',
}: CustomRecurrenceMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelectRecurrence = useCallback(
    (option: string | null) => {
      if (!option) {
        return;
      }

      setSelectedRecurrence && setSelectedRecurrence(option);
    },
    [setSelectedRecurrence],
  );

  const onOpenMenu = useCallback(() => {
    setIsOpen(true);
    onOpen && onOpen();
  }, [onOpen]);

  const onCloseMenu = useCallback(() => {
    setIsOpen(false);
    onClose && onClose();
  }, [onClose]);

  const options = ['Day', 'Week', 'Month', 'Year'];

  return (
    <Menu isLazy isOpen={isOpen} onClose={onCloseMenu} onOpen={onOpenMenu}>
      <Box position="relative" width={width} height={rem(28)} lineHeight={0}>
        <MenuButton
          as={Button}
          width={width}
          height={rem(28)}
          color="text-primary"
          opacity={readonly ? 0.5 : 1}
          border={variant === 'bordered' ? `${rem(1)} solid var(--chakra-colors-stroke-primary)` : undefined}
          borderRadius={rem(4)}
          _hover={{
            backgroundColor: 'background-quaternary',
          }}
          _active={{
            backgroundColor: 'background-secondary',
          }}
          pointerEvents={readonly ? 'none' : 'visible'}
          backgroundColor="background-primary"
          paddingX={rem(8)}
        >
          <Flex
            alignItems="center"
            justifyContent="start"
            gap={2}
            minWidth={rem(24)}
            height={rem(28)}
            borderRadius="full"
          >
            <Text
              textStyle="small"
              overflow="hidden"
              color="text-primary"
              textAlign="left"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {recurrence || options[0]}
            </Text>
          </Flex>
          <Icon
            as={IconChevronDown}
            position="absolute"
            top="50%"
            right={rem(8)}
            boxSize={rem(8)}
            minWidth={rem(16)}
            height={rem(16)}
            padding="0"
            color="white.500"
            transform={`translateY(-50%)`}
          />
        </MenuButton>
      </Box>
      <MenuList
        zIndex={3}
        overflowX="hidden"
        overflowY="auto"
        maxHeight={rem(300)}
        padding={0}
        borderRadius={4}
        boxShadow="0px 0px 10px 0px var(--chakra-colors-black-900a06)"
        backgroundColor={'gray.800'}
      >
        {options.map((option) => (
          <MenuItem
            key={option}
            as={Button}
            sx={{
              '&:hover, &:focus': {
                backgroundColor: 'background-secondary',
              },
            }}
            justifyContent="flex-start"
            fontSize="sm"
            textTransform="initial"
            borderRadius={0}
            backgroundColor="background-tertiary"
            onClick={() => handleSelectRecurrence(option)}
          >
            <Text textStyle="small" color="text-primary" noOfLines={1}>
              {option}
            </Text>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export default CustomRecurrenceMenu;
